import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  CLIENT_CONTACT_ROLE_LABELS,
  CLIENT_CONTACT_ROLES,
  type ClientContactDto,
  type ClientContactRole,
  type ClientDetailDto,
  type ClientDto,
  type SiteDto,
} from "@klantpunt/shared";
import { IconTrash } from "../../app/icons";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { Meta } from "../../app/Meta";

const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Klanten van de organisatie (docs/CLIENTS.md): eigenaren of opdrachtgevers voor wie gebouwen worden beheerd,
 * met hun locaties en contactpersonen. Gebouwen indelen en verplaatsen gaat vanuit de gebouwenlijst (Selecteren).
 */
export function ClientsSection({ canEdit }: { canEdit: boolean }) {
  const [archived, setArchived] = useState(false);
  const q = useQuery({
    queryKey: ["clients", archived ? "archived" : "active"],
    queryFn: () => api<ClientDto[]>(`/clients${archived ? "?archived=1" : ""}`),
  });
  const [open, setOpen] = useState<string | "new" | null>(null);

  return (
    <section className="panel" aria-labelledby="clients-h" aria-busy={q.isPending}>
      <div className="row">
        <h3 id="clients-h" className="grow">
          Klanten
        </h3>
        <label className="small">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Ook gearchiveerde
        </label>
        {canEdit && (
          <button type="button" className="btn primary" onClick={() => setOpen("new")}>
            Nieuwe klant
          </button>
        )}
      </div>
      <p className="hint">
        Elk gebouw hoort bij een klant. Gebouwen van vóór de klanten staan onder &ldquo;Nog in te delen&rdquo;. Indelen
        doe je op Gebouwen met &ldquo;Selecteren&rdquo; en &ldquo;Verplaatsen naar klant&rdquo;; een los gebouw
        verplaats je onder Gebouw → Gegevens.
      </p>
      {q.isPending && <Loading />}
      {q.data && (
        <div className="tscroll">
          <table className="it">
            <thead>
              <tr>
                <th scope="col">Klant</th>
                <th scope="col">Voorvoegsel</th>
                <th scope="col">Plaats</th>
                <th scope="col" className="num">
                  Gebouwen
                </th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <button type="button" className="linkbtn" onClick={() => setOpen(c.id)}>
                      {c.name}
                    </button>
                    {c.isUnassigned && <span className="muted small"> · systeemklant</span>}
                    {c.archivedAt && <span className="muted small"> · gearchiveerd</span>}
                  </td>
                  <td className="code">{c.codePrefix ?? ""}</td>
                  <td>{c.city ?? ""}</td>
                  <td className="num">{c.buildingCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open === "new" && <NewClientDialog onClose={() => setOpen(null)} onCreated={(id) => setOpen(id)} />}
      {open && open !== "new" && <ClientDialog id={open} canEdit={canEdit} onClose={() => setOpen(null)} />}
    </section>
  );
}

interface ClientForm {
  name: string;
  shortName: string;
  codePrefix: string;
  reference: string;
  address: string;
  postalCode: string;
  city: string;
  mjopIndexPct: string;
}
const emptyForm: ClientForm = {
  name: "",
  shortName: "",
  codePrefix: "",
  reference: "",
  address: "",
  postalCode: "",
  city: "",
  mjopIndexPct: "",
};
const formOf = (c: ClientDto): ClientForm => ({
  name: c.name,
  shortName: c.shortName ?? "",
  codePrefix: c.codePrefix ?? "",
  reference: c.reference ?? "",
  address: c.address ?? "",
  postalCode: c.postalCode ?? "",
  city: c.city ?? "",
  mjopIndexPct: c.mjopIndexPct == null ? "" : String(c.mjopIndexPct),
});
const bodyOf = (f: ClientForm) => ({
  ...f,
  codePrefix: f.codePrefix.trim() || null,
  mjopIndexPct: f.mjopIndexPct.trim() === "" ? null : Number(f.mjopIndexPct.replace(",", ".")),
});

function ClientFields({ f, setF, locked }: { f: ClientForm; setF: (f: ClientForm) => void; locked?: boolean }) {
  const set = (k: keyof ClientForm) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <div className="grid2">
        <label className="field">
          <span className="label">Naam *</span>
          <input value={f.name} onChange={set("name")} disabled={locked} required placeholder="Mossel" />
        </label>
        <label className="field">
          <span className="label">Korte naam</span>
          <input value={f.shortName} onChange={set("shortName")} placeholder="voor lijsten" />
        </label>
      </div>
      <div className="grid3">
        <label className="field">
          <span className="label">Voorvoegsel gebouwcodes</span>
          <input
            value={f.codePrefix}
            onChange={set("codePrefix")}
            maxLength={5}
            placeholder="MOS"
            style={{ textTransform: "uppercase" }}
          />
        </label>
        <label className="field">
          <span className="label">Klantnummer</span>
          <input value={f.reference} onChange={set("reference")} placeholder="eigen nummer" />
        </label>
        <label className="field">
          <span className="label">Indexpercentage MJOP</span>
          <input value={f.mjopIndexPct} onChange={set("mjopIndexPct")} inputMode="decimal" placeholder="organisatie" />
        </label>
      </div>
      <p className="hint">
        Nieuwe gebouwen krijgen een code met het voorvoegsel als voorstel (bijv. MOS001). Bestaande codes blijven altijd
        zoals ze zijn. Zonder indexpercentage geldt dat van de organisatie.
      </p>
      <div className="grid3">
        <label className="field">
          <span className="label">Adres</span>
          <input value={f.address} onChange={set("address")} />
        </label>
        <label className="field">
          <span className="label">Postcode</span>
          <input value={f.postalCode} onChange={set("postalCode")} />
        </label>
        <label className="field">
          <span className="label">Plaats</span>
          <input value={f.city} onChange={set("city")} />
        </label>
      </div>
    </>
  );
}

function NewClientDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<ClientForm>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const c = await api<ClientDetailDto>("/clients", { method: "POST", body: bodyOf(f) });
      await qc.invalidateQueries({ queryKey: ["clients"] });
      toast(`${c.name} toegevoegd`);
      onCreated(c.id);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Nieuwe klant"
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => save()} disabled={busy || !f.name.trim()}>
            Toevoegen
          </button>
        </>
      }
    >
      <form onSubmit={save}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <ClientFields f={f} setF={setF} />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function ClientDialog({ id, canEdit, onClose }: { id: string; canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["client", id], queryFn: () => api<ClientDetailDto>(`/clients/${id}`) });
  const [f, setF] = useState<ClientForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const c = q.data;
  const form = f ?? (c ? formOf(c) : emptyForm);

  async function refresh() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["client", id] }),
      qc.invalidateQueries({ queryKey: ["clients"] }),
    ]);
  }
  async function run(fn: () => Promise<unknown>, ok?: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      if (ok) toast(ok);
      return true;
    } catch (err) {
      setError(errMsg(err));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (await run(() => api(`/clients/${id}`, { method: "PATCH", body: bodyOf(form) }), "Klant opgeslagen")) setF(null);
  }

  return (
    <Modal
      title={c?.name ?? "Klant"}
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          {canEdit && c && !c.isUnassigned && (
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              onClick={() =>
                void run(
                  () => api(`/clients/${id}/${c.archivedAt ? "unarchive" : "archive"}`, { method: "POST" }),
                  c.archivedAt ? "Klant teruggezet" : "Klant gearchiveerd",
                )
              }
            >
              {c.archivedAt ? "Terugzetten" : "Archiveren"}
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Klaar
          </button>
          {canEdit && (
            <button type="button" className="btn primary" onClick={() => save()} disabled={busy || !f}>
              Opslaan
            </button>
          )}
        </>
      }
    >
      {!c ? (
        <Loading />
      ) : (
        <div className="stack">
          {error && (
            <div className="alert" role="alert">
              {error}
            </div>
          )}
          <p className="muted">
            {plural(c.buildingCount, "gebouw", "gebouwen")}
            {c.isUnassigned && " die nog bij een klant moeten worden ingedeeld"}
          </p>
          <form onSubmit={save}>
            <fieldset disabled={!canEdit} className="plain">
              <ClientFields f={form} setF={setF} locked={c.isUnassigned} />
            </fieldset>
            <button type="submit" hidden />
          </form>
          {!c.isUnassigned && <SitesBlock client={c} canEdit={canEdit} run={run} />}
          <ContactsBlock client={c} canEdit={canEdit} run={run} />
        </div>
      )}
    </Modal>
  );
}

type Run = (fn: () => Promise<unknown>, ok?: string) => Promise<boolean>;

function SitesBlock({ client, canEdit, run }: { client: ClientDetailDto; canEdit: boolean; run: Run }) {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  async function add(e: FormEvent) {
    e.preventDefault();
    if (
      await run(
        () => api<SiteDto>(`/clients/${client.id}/sites`, { method: "POST", body: { name, city } }),
        "Locatie toegevoegd",
      )
    ) {
      setName("");
      setCity("");
    }
  }
  return (
    <section aria-labelledby="sites-h">
      <h4 id="sites-h">Locaties</h4>
      <p className="hint">
        Een locatie is een map voor gebouwen op één plek, zoals een terrein met meerdere gebouwen. Toegang kan later ook
        per locatie.
      </p>
      {client.sites.length === 0 ? (
        <p className="muted small">Nog geen locaties.</p>
      ) : (
        <ul className="list">
          {client.sites.map((s) => (
            <li key={s.id}>
              <span className="grow">
                {s.name}
                {s.city && <span className="muted"> · {s.city}</span>}
                <span className="muted"> · {plural(s.buildingCount, "gebouw", "gebouwen")}</span>
              </span>
              {canEdit && s.buildingCount === 0 && (
                <button
                  type="button"
                  className="btn small ghost"
                  aria-label={`Locatie ${s.name} verwijderen`}
                  onClick={() => void run(() => api(`/sites/${s.id}`, { method: "DELETE" }), "Locatie verwijderd")}
                >
                  <IconTrash size={16} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form className="row" onSubmit={add}>
          <label className="field">
            <span className="label">Nieuwe locatie</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="bijv. Rotterdam" />
          </label>
          <label className="field">
            <span className="label">Plaats</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <button type="submit" className="btn" disabled={!name.trim()}>
            Locatie toevoegen
          </button>
        </form>
      )}
    </section>
  );
}

interface ContactForm {
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  role: ClientContactRole;
  isPrimary: boolean;
}
const emptyContact: ContactForm = { name: "", jobTitle: "", email: "", phone: "", role: "other", isPrimary: false };

function ContactsBlock({ client, canEdit, run }: { client: ClientDetailDto; canEdit: boolean; run: Run }) {
  const [editing, setEditing] = useState<ClientContactDto | "new" | null>(null);
  const [f, setF] = useState<ContactForm>(emptyContact);
  const set = (k: keyof ContactForm) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  function edit(c: ClientContactDto | "new") {
    setEditing(c);
    setF(
      c === "new"
        ? { ...emptyContact, isPrimary: client.contacts.length === 0 }
        : {
            name: c.name,
            jobTitle: c.jobTitle ?? "",
            email: c.email ?? "",
            phone: c.phone ?? "",
            role: c.role,
            isPrimary: c.isPrimary,
          },
    );
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    const ok = await run(
      () =>
        editing === "new"
          ? api(`/clients/${client.id}/contacts`, { method: "POST", body: f })
          : api(`/contacts/${(editing as ClientContactDto).id}`, { method: "PATCH", body: f }),
      "Contactpersoon opgeslagen",
    );
    if (ok) setEditing(null);
  }
  return (
    <section aria-labelledby="contacts-h">
      <div className="row">
        <h4 id="contacts-h" className="grow">
          Contactpersonen
        </h4>
        {canEdit && !editing && (
          <button type="button" className="btn small" onClick={() => edit("new")}>
            Contactpersoon toevoegen
          </button>
        )}
      </div>
      {client.contacts.length === 0 && !editing && <p className="muted small">Nog geen contactpersonen.</p>}
      <ul className="list">
        {client.contacts.map((c) => (
          <li key={c.id}>
            <span className="grow">
              <strong>{c.name}</strong>
              {c.isPrimary && <span className="badge"> primair</span>}
              <span className="muted">
                {" "}
                · {CLIENT_CONTACT_ROLE_LABELS[c.role]}
                {c.jobTitle && ` · ${c.jobTitle}`}
              </span>
              <br />
              <Meta className="muted small" items={[c.email, c.phone]} />
            </span>
            {canEdit && (
              <>
                <button type="button" className="btn small" onClick={() => edit(c)}>
                  Bewerken
                </button>
                <button
                  type="button"
                  className="btn small ghost"
                  aria-label={`${c.name} verwijderen`}
                  onClick={() =>
                    void run(() => api(`/contacts/${c.id}`, { method: "DELETE" }), "Contactpersoon verwijderd")
                  }
                >
                  <IconTrash size={16} aria-hidden="true" />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {editing && (
        <form className="stack" onSubmit={save} aria-label="Contactpersoon">
          <div className="grid2">
            <label className="field">
              <span className="label">Naam *</span>
              <input value={f.name} onChange={set("name")} required />
            </label>
            <label className="field">
              <span className="label">Functie</span>
              <input value={f.jobTitle} onChange={set("jobTitle")} />
            </label>
            <label className="field">
              <span className="label">E-mailadres</span>
              <input type="email" value={f.email} onChange={set("email")} />
            </label>
            <label className="field">
              <span className="label">Telefoon</span>
              <input type="tel" value={f.phone} onChange={set("phone")} />
            </label>
            <label className="field">
              <span className="label">Rol</span>
              <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as ClientContactRole })}>
                {CLIENT_CONTACT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {CLIENT_CONTACT_ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field checkfield">
              <span>
                <input
                  type="checkbox"
                  checked={f.isPrimary}
                  onChange={(e) => setF({ ...f, isPrimary: e.target.checked })}
                />{" "}
                Primair contact
              </span>
            </label>
          </div>
          <div className="btnrow">
            <button type="button" className="btn ghost" onClick={() => setEditing(null)}>
              Annuleren
            </button>
            <button type="submit" className="btn primary" disabled={!f.name.trim()}>
              Opslaan
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
